import { useState, useMemo, useEffect } from "react";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { StatsBar, StatsBarSkeleton } from "@/components/dashboard/StatsBar";
import { FilterTabs, FilterType } from "@/components/dashboard/FilterTabs";
import { LeadCard, LeadCardSkeleton } from "@/components/dashboard/LeadCard";
import { LeadDetailSheet } from "@/components/dashboard/LeadDetailSheet";
import { NewLeadDialog } from "@/components/dashboard/NewLeadDialog";
import { CreditBalanceWidget } from "@/components/dashboard/CreditBalanceWidget";
import { CreditAlertBanner } from "@/components/dashboard/CreditAlertBanner";
import { DevTools } from "@/components/dev/DevTools";
import { useAuthContext } from "@/components/auth/ClerkAuthProvider";
import { useTenantCompat as useTenant, useLeadsWithGenerationsCompat as useLeadsWithGenerations } from "@/hooks/api";
import { LeadWithGenerations } from "@/types/firestore";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, Users, Search, LinkIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LEADS_PER_PAGE = 9;

export default function Dashboard() {
  const { tenantId } = useAuthContext();
  const { data: tenant, isLoading: tenantLoading } = useTenant(tenantId ?? undefined);
  const { data: leads = [], isLoading: leadsLoading } = useLeadsWithGenerations(tenantId ?? undefined);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedLead, setSelectedLead] = useState<LeadWithGenerations | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Reset pagination when filters or search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, activeFilter]);

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        lead.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.client_phone.includes(searchQuery) ||
        lead.gemini_analysis.room_type.toLowerCase().includes(searchQuery.toLowerCase());

      // Type filter
      let matchesFilter = true;
      if (activeFilter === "kitchen") {
        matchesFilter = lead.gemini_analysis.room_type.toLowerCase() === "kitchen";
      } else if (activeFilter === "basement") {
        matchesFilter = lead.gemini_analysis.room_type.toLowerCase() === "basement";
      } else if (activeFilter === "bathroom") {
        matchesFilter = lead.gemini_analysis.room_type.toLowerCase() === "bathroom";
      } else if (activeFilter === "verified") {
        matchesFilter = lead.verification_status === "verified";
      }

      return matchesSearch && matchesFilter;
    });
  }, [leads, searchQuery, activeFilter]);

  // Paginated leads
  const paginatedLeads = useMemo(() => {
    return filteredLeads.slice(0, page * LEADS_PER_PAGE);
  }, [filteredLeads, page]);

  const hasMoreLeads = paginatedLeads.length < filteredLeads.length;
  const remainingLeads = filteredLeads.length - paginatedLeads.length;

  const handleViewDetails = (lead: LeadWithGenerations) => {
    setSelectedLead(lead);
    setDetailSheetOpen(true);
  };

  const handleRemix = (lead: LeadWithGenerations) => {
    toast.info("Remix functionality coming soon", {
      description: `Opening remix editor for ${lead.client_name}'s ${lead.gemini_analysis.room_type.toLowerCase()}...`,
    });
  };

  // Loading state with skeleton UI
  if (tenantLoading || leadsLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header skeleton */}
        <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95">
          <div className="mx-auto flex h-14 sm:h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 sm:gap-3">
              <Skeleton className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg" />
              <div>
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-16 hidden sm:block" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-10 rounded-full" />
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <StatsBarSkeleton />
          <div className="mt-6">
            <Skeleton className="h-10 w-64 mb-6" />
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <LeadCardSkeleton key={i} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  // No tenant found (shouldn't happen if auth is working)
  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Unable to load tenant data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {tenant?.is_disabled && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="font-medium">Your account has been disabled. Contact support for assistance.</span>
          </div>
        </div>
      )}

      <DashboardHeader
        tenant={tenant}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onNewLead={() => setNewLeadOpen(true)}
      />

      <main aria-label="Lead management dashboard" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Credit Alert Banner */}
        <CreditAlertBanner tenant={tenant} />

        {/* Stats */}
        <section aria-label="Dashboard statistics">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-3">
              <StatsBar leads={leads} />
            </div>
            <div className="lg:col-span-1">
              <CreditBalanceWidget tenant={tenant} />
            </div>
          </div>
        </section>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <FilterTabs activeFilter={activeFilter} onFilterChange={setActiveFilter} />
          <p className="text-sm text-muted-foreground">
            Showing {filteredLeads.length} of {leads.length} leads
          </p>
        </div>

        {/* Card Grid */}
        <section aria-label="Lead cards" className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedLeads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onViewDetails={handleViewDetails}
              onRemix={handleRemix}
            />
          ))}
        </section>

        {/* Load More Button */}
        {hasMoreLeads && (
          <div className="mt-8 flex justify-center">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setPage((p) => p + 1)}
              className="gap-2"
            >
              <ChevronDown className="h-4 w-4" />
              Load More ({remainingLeads} remaining)
            </Button>
          </div>
        )}

        {filteredLeads.length === 0 && (
          <div className="mt-12 text-center py-12 px-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              {leads.length === 0 ? (
                <Users className="h-8 w-8 text-muted-foreground" />
              ) : (
                <Search className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {leads.length === 0 ? "No leads yet" : "No matching leads"}
            </h3>
            <p className="text-muted-foreground max-w-sm mx-auto mb-6">
              {leads.length === 0
                ? "Share your widget link to start capturing renovation leads from your website."
                : "Try adjusting your filters or search terms to find what you're looking for."}
            </p>
            {leads.length === 0 && (
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/widget/${tenant?.slug}`);
                  toast.success("Widget link copied to clipboard!");
                }}
                className="gap-2"
              >
                <LinkIcon className="h-4 w-4" />
                Copy Widget Link
              </Button>
            )}
            {leads.length > 0 && searchQuery && (
              <Button
                variant="outline"
                onClick={() => setSearchQuery('')}
                className="gap-2"
              >
                <Search className="h-4 w-4" />
                Clear Search
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Detail Sheet */}
      <LeadDetailSheet
        lead={selectedLead}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        onRemix={handleRemix}
      />

      {/* New Lead Dialog */}
      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} />

      {/* Dev Tools (only in development) */}
      <DevTools currentTenantId={tenantId ?? undefined} />
    </div>
  );
}
